import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { tosThumbnail } from '../../../utils/tosImage';
import { formatEpisodeRange } from '../../../utils/episodeRange';
import {
  updateAnalysisElement,
  generateAnalysisImage,
  uploadAnalysisRefImage,
  importAnalysisImageUrl,
  clearAnalysisRefImage,
  updateAnalysisSelectedImage,
  uploadAnalysisUserImage,
  stateStylingSearch,
  stateStylingSearchMore,
  stateStylingSearchSelect,
  updateStylingTags,
} from '../../../api';
import CharacterStylePickerModal from './CharacterStylePickerModal';
import CharacterTagEditorModal from './CharacterTagEditorModal';
import { useImageUploadZone } from '../../../hooks/useImageUploadZone';
import { useImageAspectRatioGuard } from '../../../hooks/useImageAspectRatioGuard';
import { getDropHighlightStyle } from '../../../utils/imageFiles';
import { UploadZoneHint } from '../../../components/UploadZoneHint';
import { CharacterStyleCandidate, ImageModel } from '../../../types';
import ConfirmDialog from '../../../components/ConfirmDialog';
import EpisodeEditModal from '../../../components/EpisodeEditModal';
import { useToast } from '../../../components/Toast';
import type { ElementType } from '../types';
import { Lightbox } from './Lightbox';
import { EpisodeBadges } from './EpisodeBadges';
import type { ActiveImageTask, SlotFailure } from '../utils/activeImageTasks';
import { generateRequestId } from '../utils/requestId';

interface ElementCardProps {
  projectId: string;
  elementType: ElementType;
  name: string;
  imagePrompt?: string;
  imageUrls?: string[];
  refImageUrls?: string[];
  selectedImageUrl?: string;
  selectedBaseImageUrl?: string;
  activeTasks?: ActiveImageTask[];
  failedTasks?: SlotFailure[];
  episodes?: number[];
  episodeMap?: Map<number, string>;
  statePromptPrefix?: string;
  onUpdate: (type: ElementType, name: string, imagePrompt: string, imageUrls?: string[], refImageUrls?: string[], selectedImageUrl?: string, promptIncludesPrefix?: boolean) => void;
  getGenerateWarnings?: () => string[];
  onSearchSelect?: () => void;
  searchImageCount?: number;
  searchTaskProgress?: { completed: number; total: number };
  onGenerateKeywords?: () => void;
  keywordStatus?: 'none' | 'generating' | 'pending' | 'searching' | 'completed' | 'failed';
  reviewedImageStatus?: string; // 当前选中图的审核状态（active/processing），用于换图二次确认
  onReviewInvalidated?: () => void;
  // 当前项目所有可选剧集（用于 EpisodeEditModal）。为空时不展示"编辑"按钮。
  availableEpisodes?: readonly { episodeIndex: number; title?: string }[];
  // Persist-and-lift episodes after the modal saves. Parent updates its local
  // chars/scenes/props state so the badges re-render immediately.
  onEpisodesUpdate?: (nextEpisodes: number[]) => void;
  hidePromptAndRefs?: boolean;
  characterImageWorkflow?: string;
  characterName?: string;
  elementState?: string;
  onRefImagesUpdated?: () => void;
  promptIncludesPrefix?: boolean;
  appearance?: string;
  personality?: string;
  onGenerateComplete?: () => void;
  onTaskResolved?: (taskId: string) => void;
  imageModels?: ImageModel[];
  defaultImagePlatform?: string;
  defaultImageModel?: string;
}

const appendPromptsToEnd = (prompt: string, promptsToAppend: string[]) => {
  const additions = promptsToAppend.map(item => item.trim()).filter(Boolean);
  if (additions.length === 0) return prompt;
  const suffix = additions.join('\n');
  const trimmedPrompt = prompt.trimEnd();
  return trimmedPrompt ? `${trimmedPrompt}\n${suffix}` : suffix;
};

const MAX_ACTIVE_TASKS_PER_STATE = 5; // 与后端 MaxActiveImageTasksPerState 保持一致

export const ElementCard: React.FC<ElementCardProps & { children: React.ReactNode }> = ({
  projectId, elementType, name, imagePrompt, imageUrls, refImageUrls, selectedImageUrl, selectedBaseImageUrl, activeTasks, failedTasks, episodes, episodeMap, statePromptPrefix, onUpdate, children, getGenerateWarnings, onSearchSelect, searchImageCount, searchTaskProgress, onGenerateKeywords, keywordStatus, reviewedImageStatus, onReviewInvalidated, availableEpisodes, onEpisodesUpdate, hidePromptAndRefs, characterImageWorkflow, characterName, elementState, onRefImagesUpdated, promptIncludesPrefix, appearance, personality, onGenerateComplete, onTaskResolved,
  imageModels = [], defaultImagePlatform, defaultImageModel,
}) => {
  const { t } = useTranslation('projectDetail');
  const toast = useToast();
  const guardAspectRatio = useImageAspectRatioGuard();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [, setHoveredIndex] = useState<number | null>(null);
  const [currentSelectedImageUrl, setCurrentSelectedImageUrl] = useState<string | undefined>(selectedImageUrl);
  // Strip legacy frontend-added prefix if present in DB data
  const LEGACY_CHAR_PROMPT_PREFIX = '根据下面描述生成，规则：单图包含头部单独正面视图+全身三视图(正面/侧面/背面),从左到右排列,纯白色背景,布局清晰无重叠、比例协调,视图间预留均匀空白;描述：';
  const initPrompt = (p?: string) => {
    const base = p || '';
    if (statePromptPrefix && base.startsWith(statePromptPrefix)) {
      return base.slice(statePromptPrefix.length);
    }
    if (elementType === 'character' && base.startsWith(LEGACY_CHAR_PROMPT_PREFIX)) {
      return base.slice(LEGACY_CHAR_PROMPT_PREFIX.length);
    }
    return base;
  };
  const promptValueFromProps = (p?: string, hasIncludedPrefix?: boolean) => {
    if (hasIncludedPrefix && p) {
      return p;
    }
    return initPrompt(p);
  };
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptValue, setPromptValue] = useState(() => promptValueFromProps(imagePrompt, promptIncludesPrefix));
  const [includesPrefix, setIncludesPrefix] = useState(() => !!promptIncludesPrefix);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentImageUrls, setCurrentImageUrls] = useState<string[]>(imageUrls || []);
  const [currentRefImageUrls, setCurrentRefImageUrls] = useState<string[]>(Array.isArray(refImageUrls) ? refImageUrls : []);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [uploadingUserImage, setUploadingUserImage] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateWarnings, setGenerateWarnings] = useState<string[] | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>(() => {
    const matched = imageModels.find(m => m.platform === defaultImagePlatform && m.model === defaultImageModel);
    return (matched || imageModels[0])?.id || '';
  });
  // 模型列表晚到（bootstrap 异步）时补设默认；不覆盖用户已手选的值
  useEffect(() => {
    if (!selectedModelId && imageModels.length > 0) {
      const matched = imageModels.find(m => m.platform === defaultImagePlatform && m.model === defaultImageModel);
      setSelectedModelId((matched || imageModels[0]).id);
    }
  }, [imageModels, defaultImagePlatform, defaultImageModel]); // eslint-disable-line react-hooks/exhaustive-deps
  const [baseImageOptOut, setBaseImageOptOut] = useState(() => !!localStorage.getItem(`analysisElement:${projectId}:${elementType}:${name}:noBaseImage`));
  const refInputRef = useRef<HTMLInputElement>(null);
  const userImageInputRef = useRef<HTMLInputElement>(null);
  const latestRef = useRef({ promptValue, currentImageUrls, currentRefImageUrls, currentSelectedImageUrl, includesPrefix });
  latestRef.current = { promptValue, currentImageUrls, currentRefImageUrls, currentSelectedImageUrl, includesPrefix };

  const [stylingPickerOpen, setStylingPickerOpen] = useState(false);
  const [stylingCandidates, setStylingCandidates] = useState<CharacterStyleCandidate[]>([]);
  const [stylingHasMore, setStylingHasMore] = useState(false);
  const [stylingLoading, setStylingLoading] = useState(false);
  const [stylingPage, setStylingPage] = useState(1);
  const [stylingSelectedUrls, setStylingSelectedUrls] = useState<string[]>([]);
  const [stylingTags, setStylingTags] = useState<string[]>([]);
  const [stylingId, setStylingId] = useState('');
  const [stylingTagsStatus, setStylingTagsStatus] = useState('');
  const [tagEditorOpen, setTagEditorOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const stylingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stylingSearchRunRef = useRef(0);
  const activeBaseImageCount = selectedBaseImageUrl && !baseImageOptOut ? 1 : 0;
  const availableRefSlots = Math.max(0, 6 - activeBaseImageCount - currentRefImageUrls.length);
  const notifyUpdate = (
    nextPrompt: string,
    nextImageUrls?: string[],
    nextRefImageUrls?: string[],
    nextSelectedImageUrl?: string,
  ) => onUpdate(elementType, name, nextPrompt, nextImageUrls, nextRefImageUrls, nextSelectedImageUrl, includesPrefix);
  const notifyAssetUpdate = (
    nextImageUrls?: string[],
    nextRefImageUrls?: string[],
    nextSelectedImageUrl?: string,
  ) => onUpdate(elementType, name, imagePrompt || '', nextImageUrls, nextRefImageUrls, nextSelectedImageUrl, promptIncludesPrefix);
  const startEditingPrompt = () => {
    if (promptIncludesPrefix && imagePrompt) {
      setPromptValue(imagePrompt);
      setIncludesPrefix(true);
    } else if (!includesPrefix && statePromptPrefix) {
      setPromptValue(statePromptPrefix + promptValue);
      setIncludesPrefix(true);
    }
    setEditingPrompt(true);
  };

  const clearStylingPoll = () => {
    if (stylingPollRef.current) {
      clearInterval(stylingPollRef.current);
      stylingPollRef.current = null;
    }
  };

  const isStylingTagsGenerating = (tagsStatus?: string) => tagsStatus === 'running' || tagsStatus === 'pending';

  const handleCloseStylingPicker = () => {
    stylingSearchRunRef.current += 1;
    clearStylingPoll();
    setStylingLoading(false);
    setStylingPickerOpen(false);
  };

  const runStylingSearch = async () => {
    if (!projectId || !characterName) return;
    clearStylingPoll();
    const searchRun = stylingSearchRunRef.current + 1;
    stylingSearchRunRef.current = searchRun;
    setStylingLoading(true);
    setStylingCandidates([]);
    setStylingSelectedUrls([]);
    setStylingTags([]);
    setStylingId('');
    setStylingTagsStatus('');
    setStylingPage(1);
    const poll = async () => {
      try {
        const res = await stateStylingSearch(projectId, characterName, elementState || '');
        if (stylingSearchRunRef.current !== searchRun) return false;
        setStylingCandidates(res.candidates);
        setStylingHasMore(res.hasMore);
        if (res.tagsFlat) setStylingTags(res.tagsFlat);
        setStylingId(res.stylingId || '');
        setStylingTagsStatus(res.tagsStatus || '');
        if (isStylingTagsGenerating(res.tagsStatus)) {
          setStylingLoading(true);
          return true;
        }
        clearStylingPoll();
        setStylingLoading(false);
        return false;
      } catch {
        if (stylingSearchRunRef.current !== searchRun) return false;
        clearStylingPoll();
        setStylingHasMore(false);
        setStylingLoading(false);
        return false;
      }
    };
    const shouldPoll = await poll();
    if (stylingSearchRunRef.current === searchRun && shouldPoll && !stylingPollRef.current) {
      stylingPollRef.current = setInterval(poll, 3000);
    }
  };

  const handleOpenStylingSearch = async () => {
    if (!projectId || !characterName) return;
    setStylingPickerOpen(true);
    if (stylingCandidates.length > 0) return;
    await runStylingSearch();
  };

  const handleSaveStateStylingTags = async (tags: string[]) => {
    if (!projectId || !characterName) return;
    if (!stylingId) {
      // 正常 UI 下编辑按钮已 disabled;race/状态错位时给反馈而非静默 return
      toast.error(t('characterStyle.tagsSaveFailed', { error: 'styling id 缺失' }));
      return;
    }
    setSavingTags(true);
    try {
      await updateStylingTags(projectId, characterName, stylingId, tags);
      setTagEditorOpen(false);
      await runStylingSearch();
    } catch (err) {
      toast.error(t('characterStyle.tagsSaveFailed', { error: err instanceof Error ? err.message : String(err) }));
    } finally {
      setSavingTags(false);
    }
  };

  const handleStylingFetchMore = async () => {
    if (!projectId || !characterName) return;
    const nextPage = stylingPage + 1;
    setStylingLoading(true);
    try {
      const res = await stateStylingSearchMore(projectId, characterName, elementState || '', nextPage);
      if (res.candidates.length === 0) {
        toast.info(t('characterStyle.noMore'));
      } else {
        setStylingCandidates(prev => {
          const existing = new Set(prev.map(c => c.imageUrl));
          return [...prev, ...res.candidates.filter(c => !existing.has(c.imageUrl))];
        });
        setStylingPage(nextPage);
      }
      setStylingHasMore(res.hasMore);
      setStylingId(res.stylingId || '');
      setStylingTagsStatus(res.tagsStatus || '');
    } catch {
      setStylingHasMore(false);
    } finally {
      setStylingLoading(false);
    }
  };

  const handleStylingToggleUrl = (url: string) => {
    setStylingSelectedUrls(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : prev.length < availableRefSlots ? [...prev, url] : prev,
    );
  };

  const handleStylingConfirm = async (urls: string[]) => {
    if (!projectId || !characterName || urls.length === 0) return;
    const resp = await stateStylingSearchSelect(projectId, characterName, elementState || '', urls);
    const nextRefImageUrls = Array.isArray(resp.refImageUrls)
      ? resp.refImageUrls
      : Array.from(new Set([...currentRefImageUrls, ...urls]));
    const selectedStylePrompts = urls
      .map(url => stylingCandidates.find(candidate => candidate.imageUrl === url)?.stylePrompt || '')
      .filter(prompt => prompt.trim());
    const nextPrompt = appendPromptsToEnd(promptValue, selectedStylePrompts);
    if (nextPrompt !== promptValue) {
      setPromptValue(nextPrompt);
      await updateAnalysisElement(projectId, elementType, name, {
        imagePrompt: nextPrompt,
        promptIncludesPrefix: includesPrefix,
      });
    }
    setCurrentRefImageUrls(nextRefImageUrls);
    notifyUpdate(nextPrompt, currentImageUrls, nextRefImageUrls, currentSelectedImageUrl);
    handleCloseStylingPicker();
  };

  useEffect(() => () => {
    stylingSearchRunRef.current += 1;
    clearStylingPoll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingPrompt) return;
    setPromptValue(promptValueFromProps(imagePrompt, promptIncludesPrefix));
    setIncludesPrefix(!!promptIncludesPrefix);
  }, [imagePrompt, promptIncludesPrefix, editingPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (imageUrls) setCurrentImageUrls(prev => {
      const deduped = Array.from(new Set(imageUrls));
      const joined = deduped.join(',');
      if (prev.join(',') === joined) return prev;
      return deduped;
    });
  }, [imageUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentRefImageUrls(Array.isArray(refImageUrls) ? refImageUrls : []);
  }, [refImageUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentSelectedImageUrl(selectedImageUrl);
  }, [selectedImageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadRefImageFile = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    if (currentRefImageUrls.length + activeBaseImageCount >= 6) {
      toast.warning(t('elementCard.maxRefImages'));
      return;
    }
    setUploadingRef(true);
    try {
      const { refImageUrls: urls } = await uploadAnalysisRefImage(projectId, elementType, name, file);
      const safeUrls = urls || [];
      setCurrentRefImageUrls(safeUrls);
      notifyAssetUpdate(currentImageUrls, safeUrls, currentSelectedImageUrl);
    } catch (err: any) {
      toast.error(t('elementCard.uploadRefFailed', { error: err.message || err }));
    } finally {
      setUploadingRef(false);
    }
  };

  const handleRefImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) await uploadRefImageFile(file);
  };

  const handleClearRefImage = async (url?: string) => {
    setUploadingRef(true);
    try {
      await clearAnalysisRefImage(projectId, elementType, name, url);
      const updated = url ? currentRefImageUrls.filter(u => u !== url) : [];
      setCurrentRefImageUrls(updated);
      notifyAssetUpdate(currentImageUrls, updated, currentSelectedImageUrl);
    } catch (err: any) {
      toast.error(t('elementCard.clearRefFailed', { error: err.message || err }));
    } finally {
      setUploadingRef(false);
    }
  };

  const uploadUserImageFile = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    setUploadingUserImage(true);
    try {
      const { imageUrl } = await uploadAnalysisUserImage(projectId, elementType, name, file);
      const next = [...currentImageUrls, imageUrl];
      setCurrentImageUrls(next);
      notifyAssetUpdate(next, currentRefImageUrls, currentSelectedImageUrl);
    } catch (err: any) {
      toast.error(t('elementCard.uploadImageFailed', { error: err.message || err }));
    } finally {
      setUploadingUserImage(false);
    }
  };

  const handleUserImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) await uploadUserImageFile(file);
  };

  const refZone = useImageUploadZone({
    multiple: true,
    disabled: uploadingRef, // 参考图触发现有 disabled={uploadingRef}，须带进 zone
    maxFiles: Math.max(0, 6 - (currentRefImageUrls.length + activeBaseImageCount)),
    onFiles: async (imgs) => {
      for (const f of imgs) await uploadRefImageFile(f);
    },
  });
  const userImageZone = useImageUploadZone({
    onFiles: (imgs) => uploadUserImageFile(imgs[0]),
    disabled: uploadingUserImage,
  });

  const [pendingSelectUrl, setPendingSelectUrl] = useState<string | null>(null);

  const doSelectImage = async (url: string, invalidate: boolean) => {
    const next = currentSelectedImageUrl === url ? '' : url;
    setCurrentSelectedImageUrl(next || undefined);
    notifyAssetUpdate(currentImageUrls, currentRefImageUrls, next || undefined);
    try {
      await updateAnalysisSelectedImage(projectId, elementType, name, next, invalidate);
    } catch (err: any) {
      console.error('保存选中图片失败:', err);
    }
  };

  const handleSelectImage = async (url: string) => {
    // 当前选中图有通过/处理中的审核，且用户要换成另一张图时，需要二次确认
    const isChangingAway = currentSelectedImageUrl && currentSelectedImageUrl !== url;
    if (isChangingAway && reviewedImageStatus && (reviewedImageStatus === 'active' || reviewedImageStatus === 'processing')) {
      setPendingSelectUrl(url);
      return;
    }
    await doSelectImage(url, false);
  };

  const [importingBaseImage, setImportingBaseImage] = useState(false);
  const [episodesModalOpen, setEpisodesModalOpen] = useState(false);
  const baseImageAlreadyImported = !!(selectedBaseImageUrl && currentImageUrls.includes(selectedBaseImageUrl));

  const handleImportBaseImage = async () => {
    if (!selectedBaseImageUrl || baseImageAlreadyImported) return;
    setImportingBaseImage(true);
    try {
      const resp = await importAnalysisImageUrl(projectId, elementType, name, selectedBaseImageUrl);
      const mirroredUrl = resp.imageUrl || selectedBaseImageUrl;
      const next = [...currentImageUrls, mirroredUrl];
      setCurrentImageUrls(next);
      setCurrentSelectedImageUrl(mirroredUrl);
      notifyAssetUpdate(next, currentRefImageUrls, mirroredUrl);
      toast.success(t('elementCard.importedAndSelected'));
    } catch (err: any) {
      toast.error(t('elementCard.importBaseImageFailed', { error: err.message || err }));
    } finally {
      setImportingBaseImage(false);
    }
  };

  const handleSavePrompt = async () => {
    setSaving(true);
    try {
      await updateAnalysisElement(projectId, elementType, name, {
        imagePrompt: promptValue,
        promptIncludesPrefix: includesPrefix,
      });
      setEditingPrompt(false);
      notifyUpdate(promptValue, currentImageUrls, currentRefImageUrls, currentSelectedImageUrl);
    } catch (err) {
      toast.error(t('elementCard.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const noBaseImageKey = `analysisElement:${projectId}:${elementType}:${name}:noBaseImage`;

  const doGenerate = async () => {
    setGenerateError(null);
    setSubmitting(true);
    try {
      const baseUrl = (selectedBaseImageUrl && !baseImageOptOut)
        ? selectedBaseImageUrl : undefined;
      const selectedModel = imageModels.find(m => m.id === selectedModelId);
      const requestId = generateRequestId();
      await generateAnalysisImage(
        projectId, elementType, name, promptValue, baseUrl, includesPrefix,
        selectedModel?.platform, selectedModel?.model, requestId,
      );
      onGenerateComplete?.(); // 触发父级 loadAnalysis → 刷新 active-tasks，新任务以占位出现
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status;
      const code = err?.code ?? err?.body?.code;
      if (status === 409 || code === 'too_many_active_tasks') {
        toast.warning(t('elementCard.tooManyActiveTasks'));
      } else {
        setGenerateError(t('elementCard.generateRequestFailed', { error: err?.message || err }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    if (!promptValue.trim()) { setGenerateError(t('elementCard.fillImagePromptFirst')); return; }
    if (getGenerateWarnings) {
      const warnings = getGenerateWarnings();
      if (warnings.length > 0) {
        setGenerateWarnings(warnings);
        return;
      }
    }
    await doGenerate();
  };

  const hasImages = currentImageUrls.length > 0;
  const inFlightCount = (activeTasks ?? []).length;
  const atLimit = inFlightCount >= MAX_ACTIVE_TASKS_PER_STATE;

  const shownError = generateError;

  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: '6px', padding: '0.75rem' }}>
      <ConfirmDialog
        open={!!generateWarnings}
        title={t('elementCard.generateWarning.title')}
        message={generateWarnings ? (
          <div>
            {generateWarnings.map((w, i) => (
              <p key={i} style={{ margin: i === 0 ? 0 : '0.5rem 0 0' }}>⚠ {w}</p>
            ))}
            <p style={{ margin: '0.8rem 0 0', fontWeight: 500 }}>{t('elementCard.generateWarning.continueQuestion')}</p>
          </div>
        ) : ''}
        confirmText={t('elementCard.generateWarning.confirmText')}
        cancelText={t('cancel', { ns: 'common' })}
        onCancel={() => setGenerateWarnings(null)}
        onConfirm={() => { setGenerateWarnings(null); doGenerate(); }}
      />
      {children}
      {(() => {
        const hasEps = !!(episodes && episodes.length > 0);
        const canEdit = !!availableEpisodes && availableEpisodes.length > 0 && !!onEpisodesUpdate;
        if (!hasEps && !canEdit) return null;
        const rangeLabel = hasEps ? formatEpisodeRange(episodes!) : '';
        return (
          <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span
              style={{ fontSize: '0.75rem', color: 'var(--text-3)', flexShrink: 0 }}
              title={rangeLabel || undefined}
            >
              {t('elementCard.appearsInEpisodes')}
            </span>
            {hasEps ? (
              <EpisodeBadges projectId={projectId} episodes={episodes} episodeMap={episodeMap} />
            ) : (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>{t('elementCard.notLabeled')}</span>
            )}
            {canEdit && (
              <button
                onClick={() => setEpisodesModalOpen(true)}
                style={{
                  background: 'none', border: '1px solid var(--border)', cursor: 'pointer',
                  padding: '1px 8px', borderRadius: '4px', fontSize: '0.7rem',
                  color: '#6b7280', fontWeight: 500, lineHeight: 1.6,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--theme-scene)'; e.currentTarget.style.color = 'var(--theme-scene)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = '#6b7280'; }}
                title={t('elementCard.editEpisodeBtnTitle')}
              >
                {hasEps ? t('elementCard.editEpisodes') : t('elementCard.addEpisodeLabel')}
              </button>
            )}
          </div>
        );
      })()}
      {(appearance || personality) && (
        <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.5, padding: '0.4rem 0.6rem', borderRadius: '6px', background: 'color-mix(in srgb, var(--theme-character) 5%, transparent)' }}>
          {appearance && <p style={{ margin: '0 0 0.2rem' }}>{t('character.appearance')}{appearance}</p>}
          {personality && <p style={{ margin: 0 }}>{t('character.personality')}{personality}</p>}
        </div>
      )}
      {onEpisodesUpdate && availableEpisodes && (
        <EpisodeEditModal
          open={episodesModalOpen}
          title={t('elementCard.editEpisodeTitle', { name })}
          availableEpisodes={availableEpisodes}
          initialSelected={episodes || []}
          onClose={() => setEpisodesModalOpen(false)}
          onSave={async next => {
            await updateAnalysisElement(projectId, elementType, name, { episodes: next });
            onEpisodesUpdate(next);
          }}
        />
      )}
      {!hidePromptAndRefs && (
      <div style={{ marginTop: '0.5rem' }}>
        {editingPrompt ? (
          <div onClick={(e) => e.stopPropagation()}>
            <textarea
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              style={{
                width: '100%', minHeight: '80px', fontSize: '0.82rem', border: '1px solid var(--theme-scene)',
                borderRadius: '4px', padding: '0.5rem', resize: 'vertical', boxSizing: 'border-box',
                fontFamily: 'inherit', lineHeight: 1.5,
              }}
              placeholder={t('elementCard.imagePromptPlaceholder')}
            />
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
              <button
                className="btn"
                style={{ background: 'var(--theme-scene)', color: '#fff', borderColor: 'var(--theme-scene)', padding: '4px 12px', fontSize: '0.8rem' }}
                onClick={handleSavePrompt}
                disabled={saving}
              >
                {saving ? t('savingEllipsis', { ns: 'common' }) : t('save', { ns: 'common' })}
              </button>
              <button
                className="btn btn-secondary"
                style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                onClick={() => {
                  setEditingPrompt(false);
                  setPromptValue(promptValueFromProps(imagePrompt, promptIncludesPrefix));
                  setIncludesPrefix(!!promptIncludesPrefix);
                }}
                disabled={saving}
              >
                {t('cancel', { ns: 'common' })}
              </button>
              <button
                className="btn"
                style={{ background: 'var(--theme-scene)', color: '#fff', borderColor: 'var(--theme-scene)', padding: '4px 12px', fontSize: '0.8rem' }}
                onClick={async () => {
                  if (saving) return;
                  setSaving(true);
                  try {
                    await updateAnalysisElement(projectId, elementType, name, {
                      imagePrompt: promptValue,
                      promptIncludesPrefix: includesPrefix,
                    });
                    setEditingPrompt(false);
                    notifyUpdate(promptValue, currentImageUrls, currentRefImageUrls, currentSelectedImageUrl);
                  } catch { setGenerateError(t('elementCard.saveFailed')); setSaving(false); return; }
                  setSaving(false);
                  await handleGenerate();
                }}
                disabled={saving || submitting || atLimit}
                title={atLimit ? t('elementCard.tooManyActiveTasks') : undefined}
              >
                {submitting ? t('generating', { ns: 'common' }) : t('elementCard.saveAndGenerate')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ background: 'var(--primary-light)', borderRadius: '4px', padding: '0.5rem 0.75rem' }} onClick={(e) => e.stopPropagation()}>
            {(() => {
              const raw = imagePrompt || '';
              const hasProjectPrefix = statePromptPrefix && raw.startsWith(statePromptPrefix);
              const isCustomPrefix = promptIncludesPrefix && !hasProjectPrefix && raw.length > 0;
              const displayedPromptValue = promptIncludesPrefix && hasProjectPrefix
                ? raw.slice(statePromptPrefix.length)
                : promptValue;

              if (isCustomPrefix) {
                return (
                  <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.82rem', color: 'var(--theme-scene)' }}>
                    {t('elementCard.imagePromptLabel')}{promptValue}
                  </p>
                );
              }
              if (displayedPromptValue) {
                return (
                  <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.82rem', color: 'var(--theme-scene)' }}>
                    {t('elementCard.imagePromptLabel')}{statePromptPrefix && (
                      <span style={{ color: 'var(--text-3)' }}>{statePromptPrefix}</span>
                    )}{displayedPromptValue}
                  </p>
                );
              }
              return (
                <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.82rem', color: 'var(--text-3)' }}>{t('elementCard.noImagePrompt')}</p>
              );
            })()}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {imageModels.length > 0 && (
                <select
                  className="input"
                  value={selectedModelId}
                  onChange={(e) => setSelectedModelId(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  title={t('elementCard.selectModelTitle')}
                  style={{ fontSize: '0.75rem', padding: '2px 6px', height: '26px', borderColor: 'var(--theme-scene)', borderRadius: '4px', maxWidth: '160px' }}
                >
                  {imageModels.map(m => <option key={m.id} value={m.id}>{m.displayName || m.model}</option>)}
                </select>
              )}
              <button
                style={{ background: 'none', border: '1px solid var(--theme-scene)', color: 'var(--theme-scene)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                onClick={startEditingPrompt}
              >
                {t('edit', { ns: 'common' })}
              </button>
              {promptValue && (
                <button
                  style={{ background: 'none', border: '1px solid var(--theme-scene)', color: 'var(--theme-scene)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                  onClick={() => {
                    const fullText = includesPrefix ? promptValue : (statePromptPrefix || '') + promptValue;
                    navigator.clipboard.writeText(fullText);
                  }}
                >
                  {t('copy', { ns: 'common' })}
                </button>
              )}
              <button
                style={{
                  background: submitting ? 'rgba(59,130,246,0.1)' : 'var(--theme-scene)', border: 'none', color: submitting ? 'var(--theme-scene)' : 'white',
                  borderRadius: '4px', padding: '2px 10px', fontSize: '0.75rem', cursor: (submitting || atLimit) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
                onClick={handleGenerate}
                disabled={submitting || atLimit}
                title={atLimit ? t('elementCard.tooManyActiveTasks') : undefined}
              >
                {submitting && <span className="loading-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px' }}></span>}
                {submitting ? t('generating', { ns: 'common' }) : hasImages ? t('elementCard.regenerate') : t('elementCard.generateImage')}
              </button>
            </div>
            {shownError && (
              <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.78rem', color: 'var(--danger)' }}>&#10060; {shownError}</p>
            )}
          </div>
        )}
        {/* 参考图区域 */}
        <div style={{ marginTop: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
          <input ref={refInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleRefImageUpload} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'flex-end' }}>
            {/* 基础图（自动带入，可移除） */}
            {selectedBaseImageUrl && !baseImageOptOut && (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={tosThumbnail(selectedBaseImageUrl, 175)}
                  alt={t('elementCard.baseImageLabel')}
                  style={{ width: '175px', height: '175px', objectFit: 'contain', borderRadius: '4px', border: '2px solid var(--theme-scene)', display: 'block' }}
                />
                <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(37,99,235,0.75)', color: 'white', fontSize: '0.75rem', textAlign: 'center', borderRadius: '0 0 3px 3px', padding: '1px 0' }}>{t('elementCard.baseImageLabel')}</span>
                {elementType !== 'character' && (
                  <button
                    onClick={() => { localStorage.setItem(noBaseImageKey, '1'); setBaseImageOptOut(true); }}
                    style={{
                      position: 'absolute', top: '-5px', right: '-5px',
                      width: '16px', height: '16px', borderRadius: '50%',
                      background: 'var(--danger)', color: 'white', border: 'none',
                      fontSize: '10px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            {currentRefImageUrls.map((url, i) => (
              <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={tosThumbnail(url, 175)}
                  alt={`ref-${i + 1}`}
                  style={{ width: '175px', height: '175px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border)', display: 'block' }}
                />
                <button
                  onClick={() => handleClearRefImage(url)}
                  disabled={uploadingRef}
                  style={{
                    position: 'absolute', top: '-5px', right: '-5px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    background: 'var(--danger)', color: 'white', border: 'none',
                    fontSize: '10px', cursor: uploadingRef ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {availableRefSlots > 0 && (
              <button
                onClick={() => refInputRef.current?.click()}
                disabled={uploadingRef}
                {...refZone.zoneProps}
                style={{
                  width: '175px', height: '175px', flexShrink: 0,
                  background: 'none', border: '1px dashed var(--border)',
                  color: 'var(--text-3)', borderRadius: '4px',
                  cursor: uploadingRef ? 'not-allowed' : 'pointer',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '2px',
                  padding: 0,
                  ...getDropHighlightStyle(refZone.isOver),
                }}
              >
                {uploadingRef
                  ? <span className="loading-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                  : <>
                      <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: '0.8rem', lineHeight: 1, textAlign: 'center' }}>
                        {currentRefImageUrls.length > 0 ? t('elementCard.refImageLabelWithCount', { current: currentRefImageUrls.length }) : t('elementCard.refImageLabel')}
                      </span>
                      <UploadZoneHint />
                    </>
                }
              </button>
            )}
            {characterImageWorkflow === 'casting_styling' && projectId && characterName && availableRefSlots > 0 && (
              <button
                onClick={() => handleOpenStylingSearch()}
                style={{
                  width: '175px', height: '175px', flexShrink: 0,
                  background: 'none', border: '1px dashed var(--theme-scene)',
                  color: 'var(--theme-scene)', borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '2px',
                  padding: 0,
                }}
                title={t('elementCard.searchRefTitle')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--theme-scene)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <span style={{ fontSize: '0.75rem', lineHeight: 1, textAlign: 'center' }}>{t('characterStyle.stylingSearch')}</span>
              </button>
            )}
            {characterImageWorkflow !== 'casting_styling' && availableRefSlots > 0 && (() => {
              const hasKeywords = searchTaskProgress && searchTaskProgress.total > 0;
              if (hasKeywords) {
                return onSearchSelect ? (
                  <button
                    onClick={() => onSearchSelect()}
                    style={{
                      width: '175px', height: '175px', flexShrink: 0,
                      background: 'none', border: '1px dashed var(--theme-scene)',
                      color: 'var(--theme-scene)', borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: '2px',
                      padding: 0,
                    }}
                    title={t('elementCard.searchRefTitle')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--theme-scene)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <span style={{ fontSize: '0.75rem', lineHeight: 1, textAlign: 'center' }}>
                      {searchTaskProgress!.completed < searchTaskProgress!.total
                        ? t('elementCard.searchProgress', { completed: searchTaskProgress!.completed, total: searchTaskProgress!.total })
                        : t('elementCard.searchCount', { count: searchImageCount })}
                    </span>
                  </button>
                ) : null;
              }
              if (onGenerateKeywords && keywordStatus !== 'completed') {
                const isGenerating = keywordStatus === 'generating';
                return (
                  <button
                    onClick={() => { if (!isGenerating) onGenerateKeywords(); }}
                    disabled={isGenerating}
                    style={{
                      width: '175px', height: '175px', flexShrink: 0,
                      background: 'none', border: '1px dashed var(--theme-scene)',
                      color: 'var(--theme-scene)', borderRadius: '4px',
                      cursor: isGenerating ? 'wait' : 'pointer',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: '4px',
                      padding: '0.5rem',
                      opacity: isGenerating ? 0.6 : 1,
                    }}
                    title={t('search.generateKeywordsTitle')}
                  >
                    {isGenerating ? (
                      <>
                        <span className="loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                        <span style={{ fontSize: '0.75rem', lineHeight: 1.2, textAlign: 'center' }}>{t('search.generatingKeywords')}</span>
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--theme-scene)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <span style={{ fontSize: '0.75rem', lineHeight: 1.2, textAlign: 'center' }}>{t('search.generateSearchKeywords')}</span>
                      </>
                    )}
                  </button>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>
      )}

        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', padding: '6px 6px 4px' }}>
          <input ref={userImageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUserImageUpload} />
          {(activeTasks ?? []).map(task => (
            <div
              key={task.taskId}
              data-testid="inflight-placeholder"
              className="image-placeholder generating"
              style={{
                width: '250px', height: '250px', flexShrink: 0,
                border: '1px dashed var(--theme-scene)', borderRadius: '6px',
                background: 'rgba(34, 197, 94, 0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <span className="loading-spinner" style={{ width: '24px', height: '24px', borderWidth: '3px' }} />
            </div>
          ))}
          {(failedTasks ?? []).map(f => (
            <div
              key={f.taskId}
              data-testid="failed-placeholder"
              className="image-placeholder failed"
              title={f.error ? t('elementCard.generateFailed', { error: f.error }) : t('elementCard.unknownError')}
              style={{
                width: '250px', height: '250px', flexShrink: 0,
                border: '1px dashed var(--danger)', borderRadius: '6px',
                background: 'rgba(239, 68, 68, 0.08)', color: 'var(--danger)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '4px', cursor: 'help',
              }}
            >
              <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>&#10060;</span>
              <span style={{ fontSize: '0.72rem' }}>{t('elementCard.generateFailed', { error: f.error || t('elementCard.unknownError') })}</span>
            </div>
          ))}
          {(() => {
            const reversed = [...currentImageUrls].reverse();
            // 如果 selectedImageUrl 不在候选列表中，将其插入最前面以显示选中标记
            if (currentSelectedImageUrl && !currentImageUrls.includes(currentSelectedImageUrl)) {
              reversed.unshift(currentSelectedImageUrl);
            }
            return reversed;
          })().map((url, i) => {
            const isSelected = currentSelectedImageUrl === url;
            return (
              <div
                key={i}
                style={{ position: 'relative', flexShrink: 0, cursor: 'pointer' }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
              >
                <img
                  src={tosThumbnail(url, 250)}
                  alt={`${name}-${i + 1}`}
                  style={{
                    width: '250px', height: '250px', objectFit: 'contain', borderRadius: '6px', display: 'block',
                    outline: isSelected ? '4px solid var(--theme-scene)' : 'none',
                    outlineOffset: '2px',
                  }}
                />
                {/* Check circle at top-right for select/deselect */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleSelectImage(url); }}
                  style={{
                    position: 'absolute', top: '4px', right: '4px',
                    width: '22px', height: '22px', borderRadius: '50%',
                    background: isSelected ? 'var(--theme-scene)' : 'rgba(255,255,255,0.8)',
                    border: isSelected ? 'none' : '2px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: '13px', padding: 0,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    zIndex: 1,
                    color: isSelected ? 'white' : 'transparent',
                  }}
                >
                  {isSelected ? '✓' : ''}
                </button>
              </div>
            );
          })}
          {/* 上传自定义图片 */}
          <div
            onClick={(e) => { e.stopPropagation(); if (!uploadingUserImage) userImageInputRef.current?.click(); }}
            {...userImageZone.zoneProps}
            style={{
              width: '250px', height: '250px', flexShrink: 0,
              border: '1px dashed var(--border)', borderRadius: '6px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: '4px', cursor: uploadingUserImage ? 'not-allowed' : 'pointer',
              background: 'var(--surface-2)', color: 'var(--text-3)',
              ...getDropHighlightStyle(userImageZone.isOver),
            }}
          >
            {uploadingUserImage
              ? <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></span>
              : <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>+</span>
            }
            <span style={{ fontSize: '0.72rem' }}>{uploadingUserImage ? t('uploading', { ns: 'common' }) : t('elementCard.uploadImage')}</span>
            {!uploadingUserImage && <UploadZoneHint />}
          </div>
          {/* 引入基础图 */}
          {selectedBaseImageUrl && (
            <div
              onClick={(e) => { e.stopPropagation(); if (!baseImageAlreadyImported && !importingBaseImage) handleImportBaseImage(); }}
              style={{
                width: '250px', height: '250px', flexShrink: 0,
                border: `1px dashed ${baseImageAlreadyImported ? 'var(--success, #22c55e)' : 'var(--theme-scene)'}`,
                borderRadius: '6px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '4px', cursor: baseImageAlreadyImported ? 'default' : importingBaseImage ? 'not-allowed' : 'pointer',
                background: baseImageAlreadyImported ? 'rgba(34,197,94,0.06)' : 'rgba(99,102,241,0.04)',
                color: baseImageAlreadyImported ? 'var(--success, #22c55e)' : 'var(--theme-scene)',
                position: 'relative', overflow: 'hidden',
              }}
            >
              <img src={tosThumbnail(selectedBaseImageUrl, 250)} alt={t('elementCard.baseImageLabel')} style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'contain', opacity: 0.2 }} />
              <span style={{ position: 'relative', zIndex: 1 }}>
                {importingBaseImage
                  ? <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></span>
                  : <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{baseImageAlreadyImported ? '✓' : '⬇'}</span>
                }
              </span>
              <span style={{ fontSize: '0.72rem', position: 'relative', zIndex: 1, fontWeight: 500 }}>
                {importingBaseImage ? t('loading', { ns: 'common' }) : baseImageAlreadyImported ? t('elementCard.importedBaseImage') : t('elementCard.importBaseImage')}
              </span>
            </div>
          )}
        </div>
        {lightboxIndex !== null && ReactDOM.createPortal(
          <Lightbox
            urls={[...currentImageUrls].reverse()}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onChange={setLightboxIndex}
            selectedImageUrl={currentSelectedImageUrl}
            onSelect={handleSelectImage}
          />,
          document.body
        )}
        <ConfirmDialog
          open={pendingSelectUrl !== null}
          title={t('elementCard.changeReviewedImage.title')}
          message={reviewedImageStatus === 'active' ? t('elementCard.changeReviewedImage.messageApproved') : t('elementCard.changeReviewedImage.messageProcessing')}
          confirmText={t('elementCard.changeReviewedImage.confirmText')}
          cancelText={t('cancel', { ns: 'common' })}
          onConfirm={async () => {
            const url = pendingSelectUrl!;
            setPendingSelectUrl(null);
            await doSelectImage(url, true);
            toast.success(t('elementCard.changeReviewedImage.successMessage'));
            onReviewInvalidated?.();
          }}
          onCancel={() => setPendingSelectUrl(null)}
        />
      {characterImageWorkflow === 'casting_styling' && (
        <CharacterStylePickerModal
          open={stylingPickerOpen}
          title={t('characterStyle.stylingSearch')}
          candidates={stylingCandidates}
          hasMore={stylingHasMore}
          loading={stylingLoading}
          onFetchMore={handleStylingFetchMore}
          onSelect={() => {}}
          onConfirm={() => {}}
          onClose={handleCloseStylingPicker}
          multiSelect
          maxSelect={availableRefSlots}
          selectedUrls={stylingSelectedUrls}
          onToggleUrl={handleStylingToggleUrl}
          onMultiConfirm={handleStylingConfirm}
          tags={stylingTags}
          onEditTags={() => setTagEditorOpen(true)}
          editTagsDisabled={!stylingId || stylingLoading || stylingTagsStatus !== 'completed'}
        />
      )}
      {characterImageWorkflow === 'casting_styling' && (
        <CharacterTagEditorModal
          open={tagEditorOpen}
          kind="styling"
          title={t('characterStyle.editStylingTagsTitle')}
          currentTags={stylingTags}
          saving={savingTags}
          onSave={handleSaveStateStylingTags}
          onClose={() => setTagEditorOpen(false)}
        />
      )}
    </div>
  );
};

// ─── 基础图区块 ────────────────────────────────────────────────────────────────
